import React, { useState, createElement } from 'react';
import { Check } from 'lucide-react';
import { WaitlistModal } from '../components/WaitlistModal';
import { Footer } from '../components/Footer';
import { Navigation } from '../components/Navigation';
// Plan feature lists for cleaner JSX
const AIR_FEATURES_TEXT = ['Track up to 75 custom prompts', 'Monitor 2 competitors', 'Citations and sources', 'Prompts run across models daily', 'Email support'];
const PRO_FEATURES_TEXT = ['Track up to 500 custom prompts', 'Monitor 5 competitors', 'Citations and sources', 'Prompts run across models daily', 'Email support', 'Prompt grouping'];
function PlatformFeature() {
  return <span data-id="element-268">
      <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23000' d='M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z'/%3E%3C/svg%3E" alt="ChatGPT" className="inline w-[1em] h-[1em] mr-[0.25em]" style={{
      verticalAlign: '-0.1em'
    }} data-id="element-269" />
      ChatGPT and{' '}
      <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="inline w-[1em] h-[1em] mr-[0.25em]" style={{
      verticalAlign: '-0.1em'
    }} data-id="element-270" />
      Google (Gemini, AI Overview, AI Mode)
    </span>;
}
export function PricingPage() {
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [emailCopied, setEmailCopied] = useState(false);
  // Calculate prices based on billing cycle (15% off for yearly)
  const airPrice = billingCycle === 'monthly' ? 100 : 85;
  const proPrice = billingCycle === 'monthly' ? 600 : 510;
  const handleCopyEmail = async () => {
    const email = 'jason@andoe.work';
    try {
      // Modern clipboard API (primary method)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(email);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = email;
        textArea.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 3000);
    } catch (err) {
      // If all else fails, show the email in an alert
      window.prompt('Copy this email:', email);
    }
  };
  // Reusable feature list item component
  const FeatureItem = ({
    text,
    children
  }: {
    text?: string;
    children?: React.ReactNode;
  }) => <li className="flex items-start leading-snug" data-id="element-271">
      <span className="text-gray-400 mr-3 select-none" data-id="element-272">•</span>
      <span data-id="element-273">{children || text}</span>
    </li>;
  return <div className="min-h-screen bg-[#FAF9F7]" data-id="element-274">
      <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} data-id="element-275" />

      <Navigation data-id="element-276" />

      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32" data-id="element-277">
        {/* Page Header */}
        <div className="mb-8" data-id="element-278">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-id="element-279">Plans</h1>
          <p className="text-lg text-gray-600" data-id="element-280">
            Choose the plan that works best for you
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex items-center bg-[#E8E6E3] rounded-full p-1 w-fit mb-10" data-id="element-281">
          <button onClick={() => setBillingCycle('monthly')} className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'bg-transparent text-gray-600'}`} data-id="element-282">
            Pay monthly
          </button>
          <button onClick={() => setBillingCycle('yearly')} className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${billingCycle === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'bg-transparent text-gray-600'}`} data-id="element-283">
            Pay yearly
          </button>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6" data-id="element-284">
          {/* Air Plan Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col" data-id="element-285">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-286">Air</h2>

            <div className="mb-1" data-id="element-287">
              <span className="text-5xl font-bold text-gray-900" data-id="element-288">
                ${airPrice}
              </span>
              <span className="text-xl text-gray-600 ml-1" data-id="element-289">USD/mo</span>
            </div>

            {billingCycle === 'yearly' ? <p className="text-sm text-green-600 font-medium mb-3" data-id="element-290">
                Save 15% with yearly billing
              </p> : <div className="mb-3 h-5" data-id="element-291" />}

            <p className="text-gray-700 leading-relaxed mb-6 min-h-[4.5rem]" data-id="element-292">
              Best for solopreneurs or one person marketing team at early stage
              startups to track and grow AI search visibility.
            </p>

            <button className="w-full py-3 px-4 bg-gray-100 text-gray-500 rounded-full font-medium mb-6 cursor-default h-[48px] flex items-center justify-center" data-id="element-293">
              Current Plan
            </button>

            <ul className="space-y-2.5 text-gray-800 text-[15px]" data-id="element-294">
              <FeatureItem data-id="element-295">
                <PlatformFeature data-id="element-296" />
              </FeatureItem>
              {AIR_FEATURES_TEXT.map((feature, index) => <FeatureItem key={index} text={feature} data-id="element-297" />)}
            </ul>
          </div>

          {/* Pro Plan Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col" data-id="element-298">
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-299">Pro</h2>

            <div className="mb-1" data-id="element-300">
              <span className="text-5xl font-bold text-gray-900" data-id="element-301">
                ${proPrice}
              </span>
              <span className="text-xl text-gray-600 ml-1" data-id="element-302">USD/mo</span>
            </div>

            {billingCycle === 'yearly' ? <p className="text-sm text-green-600 font-medium mb-3" data-id="element-303">
                Save 15% with yearly billing
              </p> : <div className="mb-3 h-5" data-id="element-304" />}

            <p className="text-gray-700 leading-relaxed mb-6 min-h-[4.5rem]" data-id="element-305">
              Built for SMB or mid-market marketing teams to synthesize AI
              visibility signals, prioritize opportunities, and drive action
              across the team.
            </p>

            <button onClick={() => setIsWaitlistOpen(true)} className="w-full py-3 px-4 bg-gray-900 text-white rounded-full font-medium mb-6 hover:bg-gray-100 hover:text-gray-900 transition-all duration-200 h-[48px] flex items-center justify-center" data-id="element-306">
              Upgrade
            </button>

            <ul className="space-y-2.5 text-gray-800 text-[15px]" data-id="element-307">
              <FeatureItem data-id="element-308">
                <PlatformFeature data-id="element-309" />
              </FeatureItem>
              {PRO_FEATURES_TEXT.map((feature, index) => <FeatureItem key={index} text={feature} data-id="element-310" />)}
              <li className="flex items-start leading-snug" data-id="element-311">
                <span className="text-gray-400 mr-3 select-none" data-id="element-312">•</span>
                <span data-id="element-313">
                  Insights{' '}
                  <span className="ml-2 text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded" data-id="element-314">
                    Beta
                  </span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Additional Models Add-on Card */}
        <button type="button" onClick={handleCopyEmail} className="w-full bg-white rounded-2xl border border-gray-200 p-6 mb-12 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all duration-200 text-left" data-id="element-315">
          <div className="flex items-center gap-3" data-id="element-316">
            {emailCopied ? <>
                <Check className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-317" />
                <p className="text-gray-900 text-base" data-id="element-318">
                  Email copied — reach out to us!
                </p>
              </> : <>
                <span className="text-gray-400 text-xl font-light" data-id="element-319">+</span>
                <p className="text-gray-900 text-base" data-id="element-320">
                  Add Perplexity, Claude, DeepSeek, and/or Copilot for an
                  additional fee.
                </p>
              </>}
          </div>
        </button>
      </main>

      <Footer data-id="element-321" />
    </div>;
}